import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
  Scales,
  Eye,
  EyeSlash,
  CircleNotch,
  CheckCircle,
  Check,
  WarningCircle,
} from '@phosphor-icons/react';
import { getTeamInviteDetails, acceptTeamInvite, type TeamInviteDetails } from '@/lib/teamApi';
import { useAuthStore } from '@/features/auth/store';
import { routeForMember } from '@/features/auth/hooks';
import type { MemberType } from '@/features/auth/types';
import { MIN_PASSWORD_LENGTH } from '@/lib/passwordPolicy';

function passwordStrength(pw: string): { level: number; label: string; color: string } {
  if (pw.length < MIN_PASSWORD_LENGTH) return { level: 0, label: 'Too short', color: 'bg-red-400' };
  let score = 0;
  if (pw.length >= 12) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  if (score <= 1) return { level: 1, label: 'Weak', color: 'bg-orange-400' };
  if (score === 2) return { level: 2, label: 'Fair', color: 'bg-yellow-400' };
  if (score === 3) return { level: 3, label: 'Good', color: 'bg-emerald-400' };
  return { level: 4, label: 'Strong', color: 'bg-emerald-600' };
}

export default function AcceptTeamInvitePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);

  const [details, setDetails] = useState<TeamInviteDetails | null>(null);
  const [loadError, setLoadError] = useState('');
  const [loadingDetails, setLoadingDetails] = useState(true);

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [done, setDone] = useState(false);
  const [welcomeName, setWelcomeName] = useState('');

  const strength = passwordStrength(password);

  useEffect(() => {
    if (!token) { setLoadError('Invalid invitation link.'); setLoadingDetails(false); return; }
    getTeamInviteDetails(token)
      .then((d) => { setDetails(d); setLoadingDetails(false); })
      .catch((err: unknown) => {
        const msg = (err as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message;
        setLoadError(msg ?? 'This invitation link is invalid or has expired.');
        setLoadingDetails(false);
      });
  }, [token]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitError('');
    if (!token) return;
    if (password.length < MIN_PASSWORD_LENGTH) { setSubmitError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`); return; }
    if (password !== confirm) { setSubmitError('Passwords do not match.'); return; }

    setSubmitting(true);
    try {
      const session = await acceptTeamInvite({ token, password });

      setWelcomeName(`${session.user.firstName}`);
      setSession({
        accessToken: session.accessToken,
        user: {
          id: session.user.id,
          email: session.user.email,
          firstName: session.user.firstName,
          lastName: session.user.lastName,
          memberType: session.user.memberType as MemberType,
          avatarUrl: session.user.avatarUrl,
          chambersId: session.user.chambersId,
          barristerId: session.user.barristerId,
        },
        chambers: session.chambers,
        permissions: session.permissions,
      });

      setDone(true);
      setTimeout(
        () => navigate(routeForMember(session.user.memberType as MemberType), { replace: true }),
        1400,
      );
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message;
      setSubmitError(msg ?? 'Could not accept the invitation. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  // ── Loading state ──────────────────────────────────────────────
  if (loadingDetails) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f8f7f4]">
        <CircleNotch className="h-6 w-6 animate-spin text-[#9c9990]" />
      </div>
    );
  }

  // ── Invalid / expired invite ───────────────────────────────────
  if (loadError || !details) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f8f7f4]">
        <div className="max-w-sm text-center px-6">
          <div className="mb-4 flex justify-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-50">
              <WarningCircle className="h-7 w-7 text-red-500" weight="fill" />
            </div>
          </div>
          <h1 className="text-lg font-semibold text-[#1a1915]">Invitation unavailable</h1>
          <p className="mt-2 text-sm text-[#6b6961]">{loadError}</p>
          <p className="mt-4 text-xs text-[#9c9990]">
            Please ask the barrister to send a new invitation.
          </p>
        </div>
      </div>
    );
  }

  // ── Success screen ─────────────────────────────────────────────
  if (done) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f8f7f4]">
        <div className="text-center px-6">
          <div className="mb-4 flex justify-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50">
              <CheckCircle className="h-8 w-8 text-emerald-600 dark:text-emerald-400" weight="fill" />
            </div>
          </div>
          <h1 className="text-xl font-semibold text-[#1a1915]">Welcome to Jurivoxa{welcomeName ? `, ${welcomeName}` : ''}</h1>
          <p className="mt-2 text-sm text-[#6b6961]">
            You are now part of{' '}
            {details.barrister.firstName} {details.barrister.lastName}'s team.
          </p>
          <p className="mt-1 text-xs text-[#9c9990]">Taking you to your dashboard…</p>
        </div>
      </div>
    );
  }

  // ── Main invitation page ───────────────────────────────────────
  const barristerFullName = `${details.barrister.firstName} ${details.barrister.lastName}`;

  return (
    <div className="min-h-screen bg-[#f8f7f4]">
      <header className="border-b border-[#e5e3de] bg-white px-8 py-4">
        <div className="mx-auto max-w-6xl">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded bg-[#1a2f4e]">
              <Scales className="h-4 w-4 text-white" weight="duotone" />
            </div>
            <span className="text-sm font-semibold tracking-tight text-[#1a1915]">Jurivoxa</span>
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-md px-6 py-14">

        {/* Invitation summary */}
        <div className="mb-6 rounded-lg border border-[#e5e3de] bg-white p-6">
          <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[#9c9990]">
            Team invitation
          </p>
          <h1 className="mt-2 text-xl font-semibold text-[#1a1915]">
            You have been invited by
          </h1>
          <p className="mt-1 text-base font-medium text-[#1a2f4e]">{barristerFullName}</p>

          <div className="mt-4 grid grid-cols-2 gap-3">
            <div className="rounded border border-[#e5e3de] bg-[#faf9f6] px-3 py-2.5">
              <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[#9c9990]">Role</p>
              <p className="mt-0.5 text-sm font-medium text-[#1a1915]">{details.roleLabelDisplay}</p>
            </div>
            <div className="rounded border border-[#e5e3de] bg-[#faf9f6] px-3 py-2.5">
              <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[#9c9990]">Account</p>
              <p className="mt-0.5 text-sm font-medium text-[#1a1915] truncate">{details.email}</p>
            </div>
          </div>

          {details.permissionLabels.length > 0 && (
            <div className="mt-4">
              <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[#9c9990]">
                Permissions granted
              </p>
              <ul className="mt-2 space-y-1.5">
                {details.permissionLabels.map((label) => (
                  <li key={label} className="flex items-center gap-2 text-sm text-[#3d3b35]">
                    <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-emerald-50">
                      <Check className="h-2.5 w-2.5 text-emerald-600 dark:text-emerald-400" weight="bold" />
                    </span>
                    {label}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Password setup */}
        <form onSubmit={handleSubmit} className="space-y-4 rounded-lg border border-[#e5e3de] bg-white p-6">
          <div>
            <h2 className="text-sm font-semibold text-[#1a1915]">Create your password</h2>
            <p className="mt-0.5 text-xs text-[#6b6961]">
              You will use this to sign in to Jurivoxa.
            </p>
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-medium text-[#3d3b35]">Password *</label>
            <div className="relative">
              <input
                type={showPw ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={`At least ${MIN_PASSWORD_LENGTH} characters`}
                className="w-full rounded border border-[#d4d0c8] bg-[#faf9f6] py-2.5 pl-3 pr-10 text-sm text-[#1a1915] focus:border-[#1a2f4e] focus:outline-none"
                required
              />
              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9c9990]"
              >
                {showPw ? <EyeSlash className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {password && (
              <div className="mt-2">
                <div className="flex gap-1">
                  {[1, 2, 3, 4].map((i) => (
                    <div
                      key={i}
                      className={`h-1 flex-1 rounded-full transition-colors ${
                        i <= strength.level ? strength.color : 'bg-[#e5e3de]'
                      }`}
                    />
                  ))}
                </div>
                <p className="mt-1 text-xs text-[#9c9990]">{strength.label}</p>
              </div>
            )}
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-medium text-[#3d3b35]">Confirm password *</label>
            <input
              type={showPw ? 'text' : 'password'}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Repeat your password"
              className="w-full rounded border border-[#d4d0c8] bg-[#faf9f6] px-3 py-2.5 text-sm text-[#1a1915] focus:border-[#1a2f4e] focus:outline-none"
              required
            />
          </div>

          {submitError && (
            <p className="rounded bg-red-50 px-3 py-2 text-sm text-red-700 dark:text-red-300">{submitError}</p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="flex w-full items-center justify-center gap-2 rounded bg-[#1a2f4e] py-2.5 text-sm font-medium text-white hover:bg-[#243d61] disabled:opacity-60"
          >
            {submitting ? (
              <CircleNotch className="h-4 w-4 animate-spin" />
            ) : 'Accept & join team'}
          </button>
        </form>

      </main>
    </div>
  );
}
